import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Search, Sparkles, Cpu, Globe, MessageSquare, Lock, ChevronRight,
  Rocket, BookOpen, Mail, ArrowRight,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Input } from "@/components/ui/input";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
import { Seo } from "@/components/Seo";

const TOPICS = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "Getting started",
    blurb: "Open Sabzy, type, send. We sign you in passwordlessly on first message.",
    items: [
      "Visit sabzy.ai or sabzy.in and start typing — no account required to try a single message.",
      "When you send your first reply we ask for your email. A 6-digit code lands in your inbox.",
      "Every new account gets a 7-day Pro Max trial automatically — Opus 4.7, racing mode, web search, vision.",
    ],
  },
  {
    id: "plans",
    icon: Cpu,
    title: "Plans & models",
    blurb: "Four tiers. Promo codes (`SABZYAI…`) unlock upgrades.",
    items: [
      "Free — Gemini 3 Flash, GPT-5.4 Mini, GPT-5 Nano and Claude Haiku 4.5. Quick everyday answers.",
      "Standard — adds GPT-5.4, GPT-4o, Claude Sonnet 4.6, Gemini 3.1 Pro and 3.5 Flash.",
      "Pro — adds GPT-5.5, Claude Sonnet 5, Claude Opus 4.7 and Gemini 3.8 Flash.",
      "Pro Max — every model: ChatGPT Astra (GPT-6), GPT-5.6 Sol/Terra/Luna, Claude Opus 5 & 4.8, plus Racing mode.",
    ],
  },
  {
    id: "web-search",
    icon: Globe,
    title: "Live web search",
    blurb: "Cited, source-linked answers via Tavily.",
    items: [
      "Toggle the globe icon in the composer to force a search.",
      "Sabzy auto-enables search for news, factual or recent questions.",
      "Sources render as numbered citations beneath the reply.",
    ],
  },
  {
    id: "memory",
    icon: MessageSquare,
    title: "Chat history & memory",
    blurb: "Sabzy remembers facts, preferences and recurring topics.",
    items: [
      "All chats auto-save to your account and reload on every login.",
      "We distill a private memory profile so Sabzy feels like it knows you.",
      "Click 'New chat' to start fresh; older threads remain one click away.",
    ],
  },
  {
    id: "security",
    icon: Lock,
    title: "Account & security",
    blurb: "Passwordless. Sessions last 30 days.",
    items: [
      "Sign-in uses your email + a 6-digit code. No password ever stored.",
      "Email landing in spam? Mark one Sabzy email as 'Not spam' — future codes will land in the inbox.",
      "Delete your data anytime: email support@sabzy.in from your signup address.",
    ],
  },
];

export default function Help() {
  const [q, setQ] = useState("");

  const filtered = useMemo(() => {
    if (!q.trim()) return TOPICS;
    const needle = q.toLowerCase();
    return TOPICS
      .map((t) => ({
        ...t,
        items: t.items.filter((it) => it.toLowerCase().includes(needle)),
      }))
      .filter((t) =>
        t.title.toLowerCase().includes(needle) ||
        t.blurb.toLowerCase().includes(needle) ||
        t.items.length > 0
      );
  }, [q]);

  return (
    <div className="relative min-h-screen bg-background text-foreground grain">
      <Seo
        title="Help Centre — How to use Sabzy AI (ChatGPT, Claude & Gemini guide)"
        description="Complete Sabzy AI help guide: getting started, plans, web search, racing mode, multimodal vision, promo codes (SABZYAI…), passwordless OTP login."
        path="/help"
      />

      {/* BG */}
      <div className="absolute inset-0 -z-10 grid-overlay opacity-15" />
      <div className="absolute left-1/2 top-0 -z-10 h-[460px] w-[820px] -translate-x-1/2 rounded-full bg-primary/12 blur-[140px]" />

      <TopBar />

      {/* HERO */}
      <section className="mx-auto max-w-5xl px-5 pt-14 pb-10 sm:px-8 sm:pt-20 sm:pb-14">
        <div className="label-mono text-primary">[ 01 · Help centre ]</div>
        <h1
          className="mt-3 text-balance text-4xl font-light tracking-[-0.035em] sm:text-5xl lg:text-6xl"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          How can we <span className="copper-text font-medium">help</span>?
        </h1>
        <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
          Short, practical answers. If it&apos;s not here, hit the chat bubble bottom-right or visit{" "}
          <Link to="/contact" className="text-foreground underline-offset-2 hover:underline" data-testid="help-cta-contact">/contact</Link>.
        </p>

        {/* Search */}
        <div className="relative mt-7 max-w-xl" data-testid="help-search">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search help (e.g. promo code, racing, web search)"
            className="h-12 rounded-2xl border-border/70 bg-card/40 pl-11 pr-4 text-[15px] backdrop-blur-sm focus-visible:ring-2 focus-visible:ring-primary/30"
            data-testid="help-search-input"
          />
        </div>
      </section>

      {/* TOPIC GRID */}
      <section className="mx-auto max-w-5xl px-5 pb-20 sm:px-8">
        {filtered.length === 0 ? (
          <EmptyState query={q} />
        ) : (
          <div className="grid gap-5 sm:grid-cols-2">
            {filtered.map((t) => (
              <TopicCard key={t.id} topic={t} />
            ))}
          </div>
        )}

        {/* CTA Strip */}
        <div className="mt-14 grid gap-4 sm:grid-cols-2">
          <CtaCard
            icon={BookOpen}
            title="Read the FAQ"
            sub="10 of the most-asked questions, in plain English."
            cta="Open FAQ"
            to="/faq"
            testid="help-cta-faq"
          />
          <CtaCard
            icon={Mail}
            title="Talk to a human"
            sub="Bugs, billing, ideas — we read everything and reply in hours."
            cta="Contact us"
            to="/contact"
            testid="help-cta-talk"
          />
        </div>
      </section>

      <Footer />
      <ChatSupportWidget />
    </div>
  );
}

function TopicCard({ topic }) {
  const Icon = topic.icon;
  return (
    <article
      className="group relative overflow-hidden rounded-2xl border border-border/60 bg-card/40 p-6 backdrop-blur-sm transition hover:-translate-y-0.5 hover:border-primary/40 hover:bg-card/60 hover:shadow-[0_20px_60px_-20px_rgba(196,122,58,0.25)]"
      data-testid={`help-topic-${topic.id}`}
    >
      <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/5 opacity-0 blur-2xl transition-opacity group-hover:opacity-100" />

      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/12 text-primary">
          <Icon className="h-4 w-4" />
        </div>
        <h2 className="text-[17px] font-semibold tracking-tight text-foreground">{topic.title}</h2>
      </div>

      <p className="mb-4 text-[13px] leading-relaxed text-muted-foreground">{topic.blurb}</p>

      <ul className="space-y-2 text-[14px] leading-relaxed text-foreground/85">
        {topic.items.map((it, i) => (
          <li key={i} className="flex gap-2">
            <ChevronRight className="mt-1 h-3 w-3 shrink-0 text-primary" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function CtaCard({ icon: Icon, title, sub, cta, to, testid }) {
  return (
    <Link
      to={to}
      className="group relative flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-gradient-to-br from-card/60 to-card/30 p-5 transition hover:border-primary/50 hover:from-card/80"
      data-testid={testid}
    >
      <div>
        <div className="mb-1 flex items-center gap-2">
          <Icon className="h-4 w-4 text-primary" />
          <span className="text-[15px] font-semibold tracking-tight text-foreground">{title}</span>
        </div>
        <p className="text-[12px] text-muted-foreground">{sub}</p>
      </div>
      <div className="flex shrink-0 items-center gap-1 text-[12px] font-medium text-primary">
        {cta}
        <ArrowRight className="h-3.5 w-3.5 transition group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function EmptyState({ query }) {
  return (
    <div className="rounded-2xl border border-dashed border-border/60 bg-card/20 p-10 text-center" data-testid="help-empty">
      <Sparkles className="mx-auto h-6 w-6 text-primary" />
      <h3 className="mt-3 text-base font-semibold">No exact match for &ldquo;{query}&rdquo;</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Try a broader term, or{" "}
        <Link to="/contact" className="text-foreground underline-offset-2 hover:underline">message us directly</Link>.
      </p>
    </div>
  );
}

export function TopBar() {
  return (
    <header className="sticky top-0 z-40 border-b border-border/40 bg-background/75 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="static-back-home-logo">
          <Logo />
        </Link>
        <nav className="flex items-center gap-1 sm:gap-3 text-[13px] text-muted-foreground">
          <NavLink to="/help" testid="static-nav-help">Help</NavLink>
          <NavLink to="/faq" testid="static-nav-faq">FAQ</NavLink>
          <NavLink to="/contact" testid="static-nav-contact">Contact</NavLink>
          <NavLink to="/privacy" testid="static-nav-privacy">Privacy</NavLink>
          <span className="mx-1 hidden h-4 w-px bg-border/60 sm:inline-block" />
          <ThemeToggle />
        </nav>
      </div>
    </header>
  );
}

function NavLink({ to, testid, children }) {
  return (
    <Link
      to={to}
      className="rounded-md px-2.5 py-1.5 transition hover:bg-secondary/50 hover:text-foreground"
      data-testid={testid}
    >
      {children}
    </Link>
  );
}

export function Footer() {
  return (
    <footer className="border-t border-border/40">
      <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-5 py-8 sm:flex-row sm:px-8">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <Logo size={18} />
          <span>© 2026 Sabzy AI · Premium intelligence terminal</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
          <Link to="/help" className="hover:text-foreground">Help</Link>
          <Link to="/faq" className="hover:text-foreground">FAQ</Link>
          <Link to="/contact" className="hover:text-foreground">Contact</Link>
          <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
        </div>
      </div>
    </footer>
  );
}
